import { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { errorView, queryView, useWindow } from '@krp/client-runtime'
import {
  GoodEditScreen,
  aqsiGoodCard,
  aqsiGoodEdit,
  type AqsiGoodEditRequest,
  type AqsiGoodEditResult,
  type GoodEditActions,
} from '@krp/resource-aqsi-ui'

/** Итог записи словами для человека. */
function noticeOf(result: AqsiGoodEditResult): string {
  if (result.outcome === 'unchanged') return 'В кабинете AQSI уже стоят эти значения: запись не потребовалась.'
  return result.snapshotUpdated
    ? 'Записано в кабинет AQSI и подтверждено чтением карточки.'
    : 'Записано в кабинет AQSI и подтверждено чтением карточки. Снимок в нашей базе обновится при ближайшей проверке каталога.'
}

/**
 * Binding окна товара источника AQSI: rid и ключ товара — из окна. Карточка читается из кабинета живьём, правка пишет в кабинет
 * одним PUT без повторов. Черновик живёт в экране (срок `screen`), данные сервера — в кэше запросов.
 */
export function AqsiGoodBinding() {
  const windowHandle = useWindow()
  const resourceId = windowHandle.params.rid ?? ''
  const goodId = windowHandle.params.key ?? ''
  const skip = resourceId === '' || goodId === ''
  const args = useMemo(() => ({ resourceId, goodId }), [goodId, resourceId])
  const result = aqsiGoodCard.useQuery(skip ? skipToken : args, { refetchOnMountOrArgChange: true })
  const [edit, saving] = aqsiGoodEdit.useMutation()
  const [notice, setNotice] = useState<string | null>(null)
  const query = useMemo(() => queryView(result, () => false), [result])
  const saveError = useMemo(() => (saving.isError ? errorView(saving.error) : null), [saving.error, saving.isError])
  const { refetch } = result
  const { reset } = saving
  const { close, setCloseGuard } = windowHandle

  const actions = useMemo<GoodEditActions>(
    () => ({
      reload: () => {
        reset()
        setNotice(null)
        void refetch()
      },
      save: (request: AqsiGoodEditRequest) => {
        setNotice(null)
        edit({ resourceId, goodId, request })
          .unwrap()
          .then((done) => setNotice(noticeOf(done)))
          // Отказ показывает экран из состояния запроса; здесь он только не должен остаться необработанным.
          .catch(() => undefined)
      },
      dismissError: () => reset(),
      close,
      closeGuard: (guard) => setCloseGuard?.(guard),
    }),
    [close, edit, goodId, refetch, reset, resourceId, setCloseGuard],
  )

  return (
    <GoodEditScreen
      card={result.data ?? null}
      query={query}
      saving={saving.isLoading}
      saveError={saveError}
      notice={notice}
      actions={actions}
    />
  )
}
